import { RAM_PRESETS, RAM_PROFILES } from 'App/constants';

const isBrowserEnvironment = () =>
    typeof window !== 'undefined' && typeof navigator !== 'undefined';

export const isIOS = () => {
    if (!isBrowserEnvironment()) return false;

    // For browsers supporting navigator.userAgentData
    if (navigator.userAgentData) {
        if (navigator.userAgentData.platform && navigator.userAgentData.brands) {
            return navigator.userAgentData.platform === 'iOS';
        }
    }

    // Fallback for older browsers
    const userAgent = window.navigator.userAgent || navigator.userAgent;

    return /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
};

export const isMobile = () => {
    if (!isBrowserEnvironment()) return false; // assume desktop during SSR

    const ua = navigator.userAgent || navigator.vendor || window.opera;

    const isMobileUA =
        /android/i.test(ua) ||
        (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) ||
        /mobile/i.test(ua);

    const isMobilePointer = window.matchMedia('(pointer:coarse) and (max-width: 768px)').matches;

    return isMobileUA || isMobilePointer;
};

export const isMobileOrTablet = () => {
    if (!isBrowserEnvironment()) return false;
    if (isMobile()) return true;

    const ua = navigator.userAgent || navigator.vendor || window.opera || '';
    const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

    return /tablet|playbook|silk/i.test(ua) || isIPadOS;
};

export const isSamsungBrowser = () => {
    if (typeof navigator === 'undefined') return false;

    return /SamsungBrowser/i.test(navigator.userAgent || '');
};

export const isAndroidEdge = () => {
    if (typeof navigator === 'undefined') return false;

    const ua = navigator.userAgent || '';

    return /Android/i.test(ua) && /EdgA|Edg\//i.test(ua);
};

/** Browsers that may report a stale larger viewport after returning from background. */
export const needsConservativeMobileResumeMeasure = () => isSamsungBrowser() || isAndroidEdge();

export const getDeviceType = () => {
    const ua = navigator.userAgent;
    if (/mobile/i.test(ua)) {
        return 'mobile';
    } else if (/tablet/i.test(ua) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 1)) {
        return 'tablet';
    } else {
        return 'desktop';
    }
};

export const isWebView = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
    }

    const ua = navigator.userAgent || '';

    const isBot = /bot|crawler|spider|crawling|HeadlessChrome/i.test(ua);

    if (isBot) return false;

    const isAndroid = /Android/.test(ua);
    const hasWvToken = /; ?wv\)/i.test(ua);
    const versionNoChrome = /\bVersion\/[\d.]+/.test(ua) && !/Chrome\/\d+/.test(ua);
    const inAppTokens = /\bFBAN\b|\bFBAV\b|\bInstagram\b|Line\/\d+/i.test(ua);

    return isAndroid && (hasWvToken || versionNoChrome || inAppTokens);
};

export function getWindowDimensions() {
    return { width: window.innerWidth, height: window.innerHeight };
}

const toCategory = (gb) => {
    const { LOW, MEDIUMLOW, MEDIUM, HIGH, VERYHIGH, ULTRAHIGH } = RAM_PROFILES;

    if (gb < LOW.threshold) return LOW.category;
    if (gb < MEDIUMLOW.threshold) return MEDIUMLOW.category;
    if (gb < MEDIUM.threshold) return MEDIUM.category;
    if (gb < HIGH.threshold) return HIGH.category;
    if (gb < VERYHIGH.threshold) return VERYHIGH.category;

    return ULTRAHIGH.category;
};

/**
 * Get approximate device RAM (GB) + category with zero noticeable performance cost.
 * - Primary: navigator.deviceMemory (Chromium; coarse buckets like 1/2/4/8).
 * - Fallbacks (optional, cheap): hardwareConcurrency, screen pixels.
 * - Nudges only ever UP-classify when strong hints suggest high-end hardware.
 */
export function getDeviceRam(opts = RAM_PRESETS.universal) {
    const { useCores, useScreen, hiEndCores, hiEndPixels } = opts;

    // 1) deviceMemory if available (privacy-preserving, coarse, often capped at 8 on mobile)
    let gb =
        typeof navigator !== 'undefined' &&
        typeof navigator.deviceMemory === 'number' &&
        navigator.deviceMemory > 0
            ? navigator.deviceMemory
            : NaN;

    // 2) Weak baseline if deviceMemory is missing (iOS/Safari, Firefox, etc.)
    if (!Number.isFinite(gb) && useCores) {
        const cores = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 4;
        gb = cores >= hiEndCores ? 6 : cores >= 6 ? 4 : cores >= 4 ? 2 : 1;
    }

    // 3) One-way nudge using physical pixels (DPR-aware). Only bump if we’re at/under 8 GB.
    if (useScreen && Number.isFinite(gb) && gb <= 8) {
        const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
        const w = (typeof screen !== 'undefined' && screen.width) || 0;
        const h = (typeof screen !== 'undefined' && screen.height) || 0;
        const pixels = Math.round(w * dpr) * Math.round(h * dpr);
        if (pixels >= hiEndPixels) gb = Math.max(gb, 12); // promote to VERY_HIGH bucket
    }

    // 4) Final fallback if absolutely nothing was detected
    if (!Number.isFinite(gb)) gb = 2;

    return { number: gb, string: toCategory(gb) };
}
